import React, { Component } from 'react';
import throttle from 'lodash.throttle';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import qs from 'qs';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { operations as jobsOperations } from '@saucelabs/sl-module-jobs';
import { records as userRecords } from '@saucelabs/sl-module-user';
import { isSupportedFeature, constants as features } from '@saucelabs/sl-component-supported-feature';
import { is } from 'immutable';
import { TitleRow } from '@saucelabs/sl-component-breadcrumbs';

import { actions, selectors } from './../ducks/flatHeadlessJobs';
import {
  constants as filterConstants,
  functions as filterFunctions,
  operations as filterOperations,
} from './../ducks/filters';
import {
  constants as sharedConstants,
  utils as sharedUtils,
} from './../ducks/shared';
import HeadlessJobsTab from './../components/HeadlessJobsTab';
import Styles from './FlatJobsContainer.styles';

const crumbsList = [
  { name: 'Headless' }, { name: 'Test Results' },
];

class FlatHeadlessJobsContainer extends Component {
  constructor(props) {
    super(props);
    this.pollers = [];
    this.throttledFetchData = throttle(this.fetchData, sharedConstants.API_FETCH_THROTTLE);

    const ownerFromQS = qs.parse(props.location.search, { ignoreQueryPrefix: true }).owner_filter;
    props.initializeFilters({
      username: props.username,
      ownerFilter: ownerFromQS ?
        {
          type: filterConstants.FILTER_HEADLESS_TESTS,
          data: { name: ownerFromQS, label: ownerFromQS, type: 'user' },
        } :
        undefined,
      fetchUsers: props.shouldFetchOrgUsers,
      fetchTeams: props.jobsTeamSharingFeatureEnabled,
    });

    const status = props.filters.getIn([
      filterConstants.FILTER_HEADLESS_TESTS,
      filterConstants.FILTER_TYPE_STATUS,
    ]);
    const ownership = filterFunctions.getOwnershipFilter({
      filterReference: props.filters,
      filterType: filterConstants.FILTER_HEADLESS_TESTS,
      isSubaccountDashboard: props.isSubaccountDashboard,
      isOpenSauceUser: props.isOpenSauceUser,
      username: props.username,
    });

    this.fetchData({
      statusList: status,
      ownership,
    });
  }

  componentDidUpdate(prevProps) {
    const {
      filters: prevFilters,
    } = prevProps;
    const {
      filters,
      // we dont have to use previous values of those, since they won't change in filters scope
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    } = this.props;

    const prevOwnership = filterFunctions.getOwnershipFilter({
      filterReference: prevFilters,
      filterType: filterConstants.FILTER_HEADLESS_TESTS,
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    });
    const ownership = filterFunctions.getOwnershipFilter({
      filterReference: filters,
      filterType: filterConstants.FILTER_HEADLESS_TESTS,
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    });
    const prevStatus = prevFilters.getIn([
      filterConstants.FILTER_HEADLESS_TESTS,
      filterConstants.FILTER_TYPE_STATUS,
    ]);
    const statusList = filters.getIn([
      filterConstants.FILTER_HEADLESS_TESTS,
      filterConstants.FILTER_TYPE_STATUS,
    ]);

    const anyFilterChanged = (
      !is(prevStatus, statusList) ||
      (prevOwnership?.name !== ownership.name)
    );

    if (anyFilterChanged) {
      this.throttledFetchData({
        ownership,
        statusList,
        tabName: filterConstants.FILTER_HEADLESS_TESTS,
      });
    }
  }

  componentWillUnmount() {
    const { cleanup } = this.props;
    this.pollers.forEach(clearInterval);
    cleanup();
  }

  toggleStatusFilter = (status) => {
    const { trackedToggleStatus } = this.props;
    trackedToggleStatus(status, { type: 'job' });
  };

  callAPIWithIntervals(apiCalls) {
    this.pollers.forEach(clearInterval);
    apiCalls.forEach((apiCall) => apiCall());
    this.pollers = apiCalls.map((apiCall) => (
      setInterval(() => apiCall(), sharedConstants.API_FETCH_INTERVAL)
    ));
  }

  fetchData({ ownership: argOwnership, statusList }) {
    const {
      fetchAutomatedJobs,
      filters,
      isOpenSauceUser,
      isSubaccountDashboard,
      jobsTeamSharingFeatureEnabled,
      organizationId,
      username,
    } = this.props;
    const propsOwnership = filterFunctions.getOwnershipFilter({
      filterReference: filters,
      filterType: filterConstants.FILTER_HEADLESS_TESTS,
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    });
    const ownership = argOwnership || propsOwnership;
    // fetch tab specific data when tab is activated
    const pollers = [];


    const params = sharedUtils.automatedJobQueryParams({
      jobsTeamSharingFeatureEnabled, organizationId, ownership, username, statusList,
    });
    pollers.push(() => fetchAutomatedJobs({ jobsTeamSharingFeatureEnabled, ...params }));

    this.callAPIWithIntervals(pollers);
  }

  render() {
    const {
      clearStatuses,
      filters,
      filtersInitialized,
      headlessAutomatedLoading,
      isOpenSauceUser,
      isSubaccountDashboard,
      jobsTeamSharingEnabled,
      jobsTeamSharingFeatureEnabled,
      orgTeams,
      orgUsers,
      selectOwner,
      toggleStatus,
      user,
      username,
    } = this.props;

    const status = filters.getIn([
      filterConstants.FILTER_HEADLESS_TESTS,
      filterConstants.FILTER_TYPE_STATUS,
    ]);
    const ownership = filterFunctions.getOwnershipFilter({
      filterReference: filters,
      filterType: filterConstants.FILTER_HEADLESS_TESTS,
      isSubaccountDashboard,
      isOpenSauceUser,
      username,
    });
    const owners = filterFunctions.buildOwnerFilter({
      isSubaccountDashboard,
      jobsTeamSharingFeatureEnabled,
      jobsTeamSharingEnabled,
      orgTeams,
      orgUsers,
      user,
      username,
    });
    const showSeeAllButton = isSupportedFeature(features.ARCHIVES) && !isOpenSauceUser;
    const noFilters = filterFunctions.noFilterInUse({
      filters,
      filterType: filterConstants.FILTER_HEADLESS_TESTS,
      ownership,
      username,
    });

    return (
      <div
        className={Styles.wrapper}
        data-qa="headless-jobs-container"
      >
        <Helmet>
          <title>Headless | Test Results</title>
        </Helmet>
        <TitleRow list={crumbsList} />
        <HeadlessJobsTab
          automatedLoading={headlessAutomatedLoading}
          clearStatuses={(payload) => clearStatuses({
            type: filterConstants.FILTER_HEADLESS_TESTS,
            filter: payload,
          })}
          filtersInitialized={filtersInitialized}
          isOpenSauceUser={isOpenSauceUser}
          noFilters={noFilters}
          owners={owners}
          ownership={ownership}
          selectOwner={(payload) => selectOwner({
            type: filterConstants.FILTER_HEADLESS_TESTS,
            data: payload,
          })}
          showOwnersFilter={!isOpenSauceUser}
          showSeeAllButton={showSeeAllButton}
          status={status}
          toggleStatus={toggleStatus}
          trackedToggleStatus={(payload) => this.toggleStatusFilter({
            type: filterConstants.FILTER_HEADLESS_TESTS,
            data: payload,
          })}
          username={username}
        />
      </div>
    );
  }
}

FlatHeadlessJobsContainer.propTypes = {
  cleanup: PropTypes.func.isRequired,
  clearStatuses: PropTypes.func.isRequired,
  fetchAutomatedJobs: PropTypes.func.isRequired,
  filters: ImmutablePropTypes.map.isRequired,
  filtersInitialized: PropTypes.bool.isRequired,
  headlessAutomatedLoading: PropTypes.bool.isRequired,
  initializeFilters: PropTypes.func.isRequired,
  isOpenSauceUser: PropTypes.bool,
  isSubaccountDashboard: PropTypes.bool,
  jobsTeamSharingEnabled: PropTypes.bool.isRequired,
  jobsTeamSharingFeatureEnabled: PropTypes.bool.isRequired,
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  orgTeams: ImmutablePropTypes.list.isRequired,
  orgUsers: ImmutablePropTypes.list.isRequired,
  selectOwner: PropTypes.func.isRequired,
  shouldFetchOrgUsers: PropTypes.bool.isRequired,
  toggleStatus: PropTypes.func.isRequired,
  trackedToggleStatus: PropTypes.func.isRequired,
  user: ImmutablePropTypes.recordOf(userRecords.UserRecordClass).isRequired,
  username: PropTypes.string.isRequired,
};

FlatHeadlessJobsContainer.defaultProps = {
  isOpenSauceUser: false,
  isSubaccountDashboard: false,
  organizationId: null,
};

const mapStateToProps = selectors.containerSelector;
const mapDispatchToProps = {
  cleanup: actions.cleanup,
  clearStatuses: filterOperations.clearStatuses,
  initializeFilters: filterOperations.initializeFilters,
  selectOwner: filterOperations.selectOwner,
  toggleStatus: filterOperations.toggleStatus,
  trackedToggleStatus: filterOperations.trackedToggleJobFilter,
  fetchAutomatedJobs: jobsOperations.fetchAutomatedJobs,
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(FlatHeadlessJobsContainer),
);

export { FlatHeadlessJobsContainer };
